// --- React & Redux
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
// --- Actions & Hooks
import { useRightsActions } from 'datastage/store/rights/useRights';
import { useAgGridApi } from 'datastage/components/AGGrid/hooks';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';
import { useRightsGridUtils } from './utils/gridUtils';
// --- External UI Libraries
import DataGrid from '@dtci/spellbook/DataGrid';
// --- Components
import { RightsAssociateDrawer } from '../associate-drawer';
// --- Utils
import { getCatalogId, getDealId } from '../associate-drawer/associations-form/utils/validations';
import { datastageApi } from 'datastage/api';
// --- Styles
import styles from 'datastage/components/AGGrid/details-table.module.scss';

/**
 * Rights Grid
 *
 * Features:
 * - Displays rights in a data grid
 * - Integrates with Redux for rights state and filters
 * - Uses custom hooks for grid API and loading state
 * - Configures columns and sorting via utility hook
 * - Handles opening the associate drawer with right details
 *
 * @returns {JSX.Element}
 */
export const RightsGrid = () => {
    const agGridApi = useAgGridApi();
    const { updateRights, updateRightsForm } = useRightsActions();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast } = useToasts();
    const { rights } = useSelector((s) => s.rights);
    const showSpinner = useSelector((s) => s.spinner.showSpinner);

    // Handle opening the associate drawer with specific right details
    const onOpenAssociateDrawer = useCallback(
        async (data) => {
            setLoadingTrue();
            try {
                const dealId = getDealId(data);
                const deal = await datastageApi.deals(dealId);

                if (deal?.code) {
                    showErrorToast(deal?.message || 'Failed to fetch deal data.');
                }

                updateRightsForm({
                    key: 'associationForm',
                    data: {
                        deal: Array.isArray(deal) && deal.length > 0 ? deal[0] : [],
                        dealNotFound: !(Array.isArray(deal) && deal.length > 0),
                    },
                });

                const catalogId = getCatalogId(data);
                const catalog = await datastageApi.catalogs(catalogId);

                if (catalog?.code) {
                    showErrorToast(catalog?.message || 'Failed to fetch catalog data.');
                }

                updateRightsForm({
                    key: 'associationForm',
                    data: {
                        catalog: Array.isArray(catalog) && catalog.length > 0 ? catalog[0] : [],
                        catalogNotFound: !(Array.isArray(catalog) && catalog.length > 0),
                    },
                });

                updateRights({ isAssociateOpen: true, details: data });
            } catch (error) {
                showErrorToast(error?.message || 'An error occurred while fetching rights details.');
                console.error('Error opening associate drawer:', error);
            } finally {
                setLoadingFalse();
            }
        },
        [setLoadingTrue, setLoadingFalse, showErrorToast, updateRights, updateRightsForm],
    );

    const { columns } = useRightsGridUtils({
        columnHandlers: [onOpenAssociateDrawer],
    });


    return (
        <>
            <DataGrid
                className={styles.dataGrid}
                autoSizing
                agGrid={{
                    ref: agGridApi.AgGridApiRef,
                    rowData: rights,
                    headerHeight: 45,
                    defaultColDef: {
                        wrapText: true,
                        autoHeight: true,
                        suppressMenu: true,
                        resizable: true,
                        cellStyle: { 
                            paddingTop: '10px', 
                            paddingBottom: '10px',
                            wordBreak: 'normal',
                            overflowWrap: 'break-word',
                        },
                    },
                }}
                bordered="none"
                columnDefs={columns}
                sortable={false}
                striped
                noResultsMessage={showSpinner ? 'Loading...' : 'No Rights Found'}
            />
            <RightsAssociateDrawer />
        </>
    );
};
